import { cx, getUsernameInitials } from "@jugl-web/utils";
import { isTextEmojiOnly } from "@jugl-web/utils/isTextEmojiOnly";
import { FC, ReactElement, useMemo, useState } from "react";
import { InteractiveContainer } from "../InteractiveContainer";
import { ReactComponent as SmallCheckIcon } from "./icons/small-check.svg";

export type AvatarSize =
  | "xs"
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl"
  | "4xl"
  | "5xl";

export interface AvatarProps {
  username: string;
  size?: AvatarSize;
  imageUrl?: string | null;
  isSelected?: boolean;
  className?: string;
  labelClassName?: string;
  isDraggable?: boolean;
  onClick?: () => void;
}

const sizeToClasses: Record<AvatarSize, string> = {
  xs: "h-4 w-4",
  sm: "h-6 w-6",
  md: "h-8 w-8",
  lg: "h-12 w-12",
  xl: "h-14 w-14",
  "2xl": "h-16 w-16",
  "3xl": "h-[72px] w-[72px]",
  "4xl": "w-20 h-20",
  "5xl": "h-[150px] w-[150px]",
};

const sizeToTextClasses: Record<AvatarSize, string> = {
  xs: "text-[10px]",
  sm: "text-xs",
  md: "text-xs",
  lg: "text-base",
  xl: "text-lg",
  "2xl": "text-xl",
  "3xl": "text-2xl",
  "4xl": "text-3xl",
  "5xl": "text-4xl",
};

export interface AvatarContainerProps {
  children: ReactElement;
  size?: AvatarSize;
  isSelected?: boolean;
  className?: string;
  onClick?: () => void;
}

export const AvatarContainer: FC<AvatarContainerProps> = ({
  children,
  size = "md",
  isSelected = false,
  className,
  onClick,
}) => (
  <div className="relative inline-flex shrink-0">
    <InteractiveContainer
      className={cx(
        "relative inline-flex overflow-hidden rounded-full",
        isSelected && "outline outline-1 outline-primary",
        sizeToClasses[size],
        className
      )}
      isDisabled={!onClick}
      onClick={onClick}
    >
      {children}
    </InteractiveContainer>
    {isSelected && (
      <div className="absolute right-0 bottom-0 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-primary">
        <SmallCheckIcon />
      </div>
    )}
  </div>
);

export const Avatar: FC<AvatarProps> = ({
  username,
  size = "md",
  imageUrl,
  className,
  labelClassName,
  isSelected = false,
  isDraggable = false,
  onClick,
}) => {
  const [hasImageLoadingError, setHasImageLoadingError] = useState(false);

  const hasImage = !!imageUrl;

  const initials = useMemo(() => getUsernameInitials(username), [username]);

  const isEmojiOnly = useMemo(() => isTextEmojiOnly(initials), [initials]);

  return (
    <AvatarContainer
      size={size}
      isSelected={isSelected}
      className={className}
      onClick={onClick}
    >
      {hasImage && !hasImageLoadingError ? (
        <img
          src={imageUrl}
          alt={username}
          className="h-full w-full object-cover"
          onError={() => setHasImageLoadingError(true)}
          draggable={isDraggable}
        />
      ) : (
        <span
          className={cx(
            "flex h-full w-full items-center justify-center text-base leading-3 font-semibold whitespace-nowrap text-dark uppercase",
            isEmojiOnly && "scale-125",
            sizeToTextClasses[size],
            labelClassName
          )}
          style={{
            background: "linear-gradient(180deg, #CECED3 0%, #ECEFF0 69.27%)",
          }}
        >
          {initials}
        </span>
      )}
    </AvatarContainer>
  );
};
